import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password, confirm_password: confirm });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not reset your password. The link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-5 py-16"
      data-testid="reset-password-page">
      <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-3">
        <img src="/logo-icon.png" alt="" className="h-10 w-auto" />
        <span className="font-heading text-3xl font-semibold tracking-tight text-primary">BareFootage</span>
      </button>
      <div className="w-full max-w-[420px] rounded-2xl border border-border bg-popover/60 backdrop-blur-xl p-7 shadow-2xl shadow-black/40">
        {done ? (
          <div className="text-center space-y-5">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <p className="text-muted-foreground">Password updated. You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/login")} data-testid="reset-goto-login"
              className="rounded-full font-medium w-full">Go to sign in</Button>
          </div>
        ) : !token ? (
          <p className="text-sm text-destructive text-center">This reset link is invalid.</p>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1">
              <h2 className="font-heading text-2xl text-primary">Set a new password</h2>
              <p className="text-sm text-muted-foreground">Choose a strong password of at least 8 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-password">New password</Label>
              <Input id="rp-password" type="password" data-testid="reset-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-confirm">Confirm new password</Label>
              <Input id="rp-confirm" type="password" data-testid="reset-password-confirm-input" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="reset-submit-button"
              className="w-full rounded-full font-medium">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Update password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
